import { WEBSOCKET_URL } from "config";

class WebSocketConnection {
  private socket: WebSocket | null;
  private messageCallback: ((message: string) => void) | null;
  private isConnecting: boolean;
  private isRequestPending: boolean;
  private latestPayload: string | null;
  private reconnectInterval: number | NodeJS.Timeout | null;
  /** Flag to distinguish intentional close() from unexpected disconnect */
  private closedIntentionally: boolean;

  constructor() {
    this.socket = null;
    this.messageCallback = null;
    this.isConnecting = false;
    this.isRequestPending = false;
    this.latestPayload = null;
    this.reconnectInterval = null;
    this.closedIntentionally = false;
  }

  connect(onopen?: () => void, onerror?: (event: Event) => void, onclose?: () => void): void {
    if (this.isConnecting) {
      return;
    }

    this.closedIntentionally = false;
    this.isConnecting = true;

    // Close any previously open socket before opening a new one. Otherwise a
    // stale socket keeps delivering messages to the shared callback and its
    // late onclose/onerror can trigger an unwanted reconnect/regenerate.
    this.disposeSocket();

    this.socket = new WebSocket(WEBSOCKET_URL);

    this.socket.onopen = () => {
      console.log("🟢 WebSocket connected");
      this.isConnecting = false;

      if (this.reconnectInterval) {
        clearInterval(this.reconnectInterval as NodeJS.Timeout);
        this.reconnectInterval = null;
      }

      onopen?.();
    };

    this.socket.onmessage = (event: MessageEvent) => {
        const message = event.data;
        this.isRequestPending = false;

        if (this.messageCallback) {
          this.messageCallback(message);
        }
    };

    this.socket.onclose = () => {
      console.log("🔴 WebSocket disconnected");
      this.isConnecting = false;

      if (!this.closedIntentionally) {
        this.reconnect();
      }

      onclose?.();
    };

    this.socket.onerror = (event: Event) => {
      this.isConnecting = false;

      if (!this.closedIntentionally) {
        this.reconnect();
      }

      onerror?.(event);
    };
  }

  send(message: string): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.isRequestPending = true;
      this.latestPayload = null;
      this.socket.send(message);
    } else {
      console.log("🟡 WebSocket connection not established");
    }
  }

  close(): void {
    this.closedIntentionally = true;

    if (this.reconnectInterval) {
      clearInterval(this.reconnectInterval as NodeJS.Timeout);
      this.reconnectInterval = null;
    }

    this.disposeSocket();
    this.isConnecting = false;
  }

  setMessageCallback(callback: ((message: string) => void) | null): void {
    this.messageCallback = callback;
  }

  private disposeSocket(): void {
    if (!this.socket) {
      return;
    }

    // Detach handlers so a closed/replaced socket can't invoke the shared
    // callback or trigger a reconnect after it is intentionally closed.
    this.socket.onmessage = null;
    this.socket.onerror = null;
    this.socket.onclose = null;

    try {
      this.socket.close();
    } catch (e) {
      // Ignore errors when closing an already-closed socket.
    }

    this.socket = null;
  }

  private reconnect(): void {
    if (!this.reconnectInterval) {
      this.reconnectInterval = setInterval(() => {
        console.log("Attempting to reconnect...");
        this.connect();
      }, 5000);
    }
  }
}

export const websocketConnection = new WebSocketConnection();
